/**
 * TestDetailPane.tsx
 * Admin → Test Mapping (master-detail redesign, v1.0.34) — the right pane:
 * the "full page for one test". Shows the selected lab test's status, an
 * Autocomplete picker over every catalog item (grouped by lab section),
 * suggested matches for unmapped tests (one-click Accept), the mapping's
 * Confirmed switch / Notes / Unmap, and the catalog item's task-type recipe
 * (ChainExplorerPanel).
 *
 * All mutations are delegated to the page shell (which owns the confirm
 * dialogs); data shapes come from services/testMappingService.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete, Box, Button, Chip, CircularProgress, Divider, FormControlLabel,
  Switch, TextField, Typography, alpha, useTheme,
} from '@mui/material';
import { Check, LinkOff, Rule } from '@mui/icons-material';
import type {
  MappingStudioCatalogItem,
  MappingStudioTest,
  MappingSuggestion,
} from '../../../services/testMappingService';
import { deriveTestStatus, type MappingStats } from './mappingModel';
import { TestStatusChip } from './TestListColumn';
import { ChainExplorerPanel } from './ChainExplorerPanel';

interface TestDetailPaneProps {
  test: MappingStudioTest | null;
  catalogItems: MappingStudioCatalogItem[];
  stats: MappingStats;
  /** Suggestion row for THIS test (unresolved tests only), if loaded. */
  suggestion: MappingSuggestion | null;
  suggestionsLoading: boolean;
  /** Idempotent — fetches the suggestions cache once per page visit. */
  onLoadSuggestions: () => void;
  /** Map / remap (page shell confirms when replacing an existing mapping). */
  onPickCatalogItem: (testKey: string, testCatalogItemKey: string) => void;
  /** Unmap (page shell confirms). */
  onUnmap: (test: MappingStudioTest) => void;
  onToggleConfirmed: (mapKey: string, isConfirmed: boolean) => void;
  onSaveNotes: (mapKey: string, notes: string | null) => void;
}

export const TestDetailPane = ({
  test,
  catalogItems,
  stats,
  suggestion,
  suggestionsLoading,
  onLoadSuggestions,
  onPickCatalogItem,
  onUnmap,
  onToggleConfirmed,
  onSaveNotes,
}: TestDetailPaneProps) => {
  const theme = useTheme();

  const status = test ? deriveTestStatus(test) : null;
  const mapKey = test?.mapping?.TestCatalogMapKey ?? null;
  const savedNotes = test?.mapping?.Notes ?? '';
  const [notes, setNotes] = useState(savedNotes);

  // Re-seed the notes draft whenever the selected test / mapping row changes.
  useEffect(() => { setNotes(savedNotes); }, [savedNotes, mapKey]);

  // Unmapped test selected → warm the suggestions cache (one round trip total).
  useEffect(() => {
    if (status === 'unmapped') onLoadSuggestions();
  }, [status, onLoadSuggestions]);

  const catalogByKey = useMemo(
    () => new Map(catalogItems.map((c) => [c.TestCatalogItemKey, c])),
    [catalogItems],
  );

  /** Options sorted by section then name — required by Autocomplete groupBy. */
  const options = useMemo(() => [...catalogItems].sort((a, b) =>
    (a.LabSection ?? 'zz').localeCompare(b.LabSection ?? 'zz')
    || a.TestName.localeCompare(b.TestName)), [catalogItems]);

  if (!test) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', p: 4, textAlign: 'center' }}>
        <Rule sx={{ fontSize: 44, color: theme.palette.text.disabled, mb: 1 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Pick a lab test on the left
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mt: 0.5 }}>
          Each lab test must map to a scheduling catalog item — that link is what turns its
          work-order lines into schedule tasks. {stats.mappedCount} of {stats.totalTests} tests
          are mapped{stats.unmappedCount > 0
            ? `; ${stats.unmappedCount} unmapped (${stats.unmappedWoLines} work-order line${stats.unmappedWoLines === 1 ? '' : 's'} affected)`
            : ''}.
        </Typography>
      </Box>
    );
  }

  const currentItem = test.mapping ? catalogByKey.get(test.mapping.TestCatalogItemKey) ?? null : null;
  const resolvedKey = test.mapping?.TestCatalogItemKey ?? test.exactNameMatchKey;
  const resolvedItem = resolvedKey ? catalogByKey.get(resolvedKey) ?? null : null;

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
          {test.TestName}
        </Typography>
        <Chip
          size="small"
          label={`${test.woLineCount} work-order line${test.woLineCount === 1 ? '' : 's'}`}
          sx={{
            height: 20,
            fontSize: '0.65rem',
            fontWeight: 600,
            backgroundColor: alpha(theme.schedule.machineTime, 0.1),
            color: theme.schedule.machineTime,
          }}
        />
        {status && <TestStatusChip status={status} />}
      </Box>

      {/* Mapping picker */}
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem', mb: 0.75 }}>
          Maps to catalog item
        </Typography>
        <Autocomplete
          size="small"
          options={options}
          value={currentItem}
          groupBy={(o) => o.LabSection ?? 'No lab section'}
          getOptionLabel={(o) => o.TestName}
          isOptionEqualToValue={(o, v) => o.TestCatalogItemKey === v.TestCatalogItemKey}
          onChange={(_e, value) => {
            if (value) onPickCatalogItem(test.TestKey, value.TestCatalogItemKey);
            else if (test.mapping) onUnmap(test);
          }}
          renderOption={(props, o) => (
            <Box component="li" {...props} key={o.TestCatalogItemKey}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" noWrap sx={{ fontSize: '0.8rem' }}>
                  {o.TestName}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.65rem',
                    color: o.taskTypeCount === 0
                      ? theme.schedule.conflictSoft
                      : theme.palette.text.secondary,
                  }}
                >
                  {o.taskTypeCount === 0
                    ? 'no recipe — generates no schedule tasks'
                    : `${o.taskTypeCount} task type${o.taskTypeCount === 1 ? '' : 's'}`}
                </Typography>
              </Box>
            </Box>
          )}
          renderInput={(params) => (
            <TextField {...params} placeholder="Type to search 99 catalog items…" />
          )}
        />
        {status === 'nameMatch' && resolvedItem && (
          <Typography variant="caption" sx={{ color: theme.admin.mappingNameMatch, display: 'block', mt: 0.5 }}>
            Resolves to &ldquo;{resolvedItem.TestName}&rdquo; by exact name match — no mapping row
            exists. Pick it above to pin the link explicitly.
          </Typography>
        )}
        {status === 'unmapped' && (
          <Typography variant="caption" sx={{ color: theme.admin.mappingUnconfirmed, display: 'block', mt: 0.5, fontWeight: 600 }}>
            Not schedulable — work-order lines on this test generate no schedule tasks until it
            is mapped.
          </Typography>
        )}
      </Box>

      {/* Suggested matches (unresolved tests only — the server scores by name) */}
      {status === 'unmapped' && (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem', mb: 0.5 }}>
            Suggested matches
          </Typography>
          {suggestionsLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">Scoring catalog names…</Typography>
            </Box>
          )}
          {!suggestionsLoading && suggestion && suggestion.candidates.length === 0 && (
            <Typography variant="caption" sx={{ color: theme.schedule.conflictSoft }}>
              No catalog item is close enough by name — this test likely needs a NEW catalog item
              (with a recipe) before it can be scheduled.
            </Typography>
          )}
          {!suggestionsLoading && suggestion?.candidates.map((c) => (
            <Box key={c.testCatalogItemKey} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.4 }}>
              <Chip
                size="small"
                label={`${Math.round(c.score * 100)}%`}
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  backgroundColor: alpha(theme.admin.mappingSuggestion, 0.14),
                  color: theme.admin.mappingSuggestion,
                }}
              />
              <Typography variant="body2" sx={{ fontSize: '0.8rem', flex: 1, minWidth: 0 }} noWrap>
                {c.testName}
              </Typography>
              <Button
                size="small"
                startIcon={<Check />}
                onClick={() => onPickCatalogItem(test.TestKey, c.testCatalogItemKey)}
                sx={{ textTransform: 'none', minWidth: 0, px: 1, fontWeight: 600 }}
              >
                Accept
              </Button>
            </Box>
          ))}
          {!suggestionsLoading && suggestion && suggestion.candidates.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
              Accepting creates an <b>unconfirmed</b> mapping — flag it confirmed once the lab
              blesses it.
            </Typography>
          )}
        </Box>
      )}

      {/* Mapping details (only when a map row exists) */}
      {test.mapping && (
        <Box>
          <Divider sx={{ mb: 1 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <FormControlLabel
              control={(
                <Switch
                  size="small"
                  checked={test.mapping.IsConfirmed}
                  onChange={(e) => onToggleConfirmed(test.mapping!.TestCatalogMapKey, e.target.checked)}
                />
              )}
              label={(
                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                  Confirmed by the lab
                </Typography>
              )}
            />
            <Box sx={{ flex: 1 }} />
            <Button
              size="small"
              color="error"
              startIcon={<LinkOff />}
              onClick={() => onUnmap(test)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Unmap
            </Button>
          </Box>
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            label="Notes"
            placeholder="Why this mapping? (e.g. lab-confirmed rename, best-guess estimate…)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={() => {
              const trimmed = notes.trim();
              if (trimmed !== (savedNotes ?? '').trim()) {
                onSaveNotes(test.mapping!.TestCatalogMapKey, trimmed === '' ? null : trimmed);
              }
            }}
            sx={{ mt: 1 }}
          />
        </Box>
      )}

      {/* Recipe of the resolved catalog item */}
      {resolvedItem && <ChainExplorerPanel catalogItem={resolvedItem} />}
    </Box>
  );
};
